import React, { useEffect, useMemo, useState } from 'react';
import { Filter, Download, Search, Trash, Eye, Loader2, X, Ban, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchUsers, deleteUser, updateUserStatus, User } from '../../services/MasterAdmin/userService';

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [updatingStatusUserId, setUpdatingStatusUserId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [platformFilter, setPlatformFilter] = useState('all');

  const platformOptions = useMemo(() => {
    const platforms = new Set<string>();
    users.forEach((user) => {
      const rawPlatforms = user.platforms ?? '';
      rawPlatforms
        .split(',')
        .map((platform) => platform.trim().toLowerCase())
        .filter(Boolean)
        .forEach((platform) => platforms.add(platform));
    });
    return Array.from(platforms).sort();
  }, [users]);

  const hasActiveFilters = statusFilter !== 'all' || platformFilter !== 'all';

  useEffect(() => {
    const getUsers = async () => {
      try {
        setLoading(true);
        const response = await fetchUsers();
        if (response.success) {
          setUsers(response.data);
          setFilteredUsers(response.data);
        } else {
          setError('Failed to fetch users');
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    getUsers();
  }, []);

  // Apply search and selected filters together so each control narrows the same result set.
  useEffect(() => {
    const searchLower = searchText.trim().toLowerCase();
    const filtered = users.filter((user) => {
      const name = user.name ?? '';
      const email = user.emailId ?? '';
      const location = user.location ?? '';
      const platforms = user.platforms ?? '';
      const phone = user.phone_number ?? '';

      const matchesSearch =
        !searchLower ||
        name.toLowerCase().includes(searchLower) ||
        email.toLowerCase().includes(searchLower) ||
        location.toLowerCase().includes(searchLower) ||
        platforms.toLowerCase().includes(searchLower) ||
        phone.toLowerCase().includes(searchLower);

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && user.status) ||
        (statusFilter === 'inactive' && !user.status);

      const userPlatforms = platforms
        .split(',')
        .map((platform) => platform.trim().toLowerCase())
        .filter(Boolean);
      const matchesPlatform =
        platformFilter === 'all' || userPlatforms.includes(platformFilter);

      return matchesSearch && matchesStatus && matchesPlatform;
    });
    setFilteredUsers(filtered);
  }, [platformFilter, searchText, statusFilter, users]);

  const clearFilters = () => {
    setStatusFilter('all');
    setPlatformFilter('all');
  };

  const formatExportDate = (value?: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
  };

  const escapeExcelCell = (value: string | number | boolean | null | undefined) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const handleExportUsers = () => {
    const columns = ['Name', 'Phone', 'Email', 'Location', 'Status', 'Platform', 'Last Active', 'Created At'];
    const rows = filteredUsers.map((user) => [
      user.name || '',
      user.phone_number || '',
      user.emailId || '',
      user.location || user.city || '',
      user.status ? 'Active' : 'Inactive',
      user.platforms || '',
      formatExportDate(user.updatedAt),
      formatExportDate(user.createdAt),
    ]);

    const tableHead = columns.map((column) => `<th>${escapeExcelCell(column)}</th>`).join('');
    const tableBody = rows
      .map(
        (row) =>
          `<tr>${row.map((cell) => `<td>${escapeExcelCell(cell)}</td>`).join('')}</tr>`
      )
      .join('');
    const worksheet = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8" />
          <!--[if gte mso 9]>
          <xml>
            <x:ExcelWorkbook>
              <x:ExcelWorksheets>
                <x:ExcelWorksheet>
                  <x:Name>Users</x:Name>
                  <x:WorksheetOptions><x:DisplayGridlines /></x:WorksheetOptions>
                </x:ExcelWorksheet>
              </x:ExcelWorksheets>
            </x:ExcelWorkbook>
          </xml>
          <![endif]-->
        </head>
        <body>
          <table>
            <thead><tr>${tableHead}</tr></thead>
            <tbody>${tableBody}</tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([worksheet], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const date = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.download = `vedika-users-${date}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleViewUser = (user: User) => {
    navigate(`/users/${user.userId}`, { state: { user } });
  };

  const handleDeleteClick = (user: User, e: React.MouseEvent) => {
    e.stopPropagation();
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      setIsDeleting(true);
      setDeleteError(null);
      
      const response = await deleteUser(userToDelete.userId);
      
      if (response.success) {
        // Remove the deleted user from the state
        setUsers(prevUsers => prevUsers.filter(u => u.userId !== userToDelete.userId));
        setFilteredUsers(prevUsers => prevUsers.filter(u => u.userId !== userToDelete.userId));
        
        // Close the modal
        setIsDeleteModalOpen(false);
        setUserToDelete(null);
      } else {
        setDeleteError(response.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setDeleteError('An error occurred while deleting the user');
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setUserToDelete(null);
    setDeleteError(null);
  };

  const handleToggleBlock = async (targetUser: User, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setUpdatingStatusUserId(targetUser.userId);
      const nextStatus = !targetUser.status;
      await updateUserStatus(targetUser.userId, nextStatus);
      setUsers((prev) =>
        prev.map((u) => (u.userId === targetUser.userId ? { ...u, status: nextStatus } : u))
      );
      setFilteredUsers((prev) =>
        prev.map((u) => (u.userId === targetUser.userId ? { ...u, status: nextStatus } : u))
      );
    } catch (err) {
      console.error('Error updating user block status:', err);
      setError('Failed to update user status. Please try again.');
    } finally {
      setUpdatingStatusUserId(null);
    }
  };

  return (
    <div className="w-full min-w-0 space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/30 overflow-hidden">
        <div className="p-4 border-b dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-2">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full min-w-0 max-w-full py-2 pl-10 pr-4 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:max-w-xs md:max-w-sm"
                  placeholder="Search users..."
                />
                {searchText && (
                  <button
                    onClick={() => setSearchText('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowFilters((value) => !value)}
                  className={`relative p-2 border rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 ${
                    hasActiveFilters
                      ? 'border-primary-500 bg-primary-50 text-primary-700 dark:border-primary-400 dark:bg-primary-900/30 dark:text-primary-300'
                      : 'border-gray-300 bg-white text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}
                  aria-label="Filter users"
                  aria-expanded={showFilters}
                >
                  <Filter className="w-4 h-4" />
                  {hasActiveFilters && (
                    <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-primary-600 dark:bg-primary-300" />
                  )}
                </button>

                {showFilters && (
                  <div className="absolute left-0 z-30 mt-2 w-64 rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Filters</h3>
                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Status
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                      className="mb-4 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="all">All users</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>

                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Platform
                    </label>
                    <select
                      value={platformFilter}
                      onChange={(e) => setPlatformFilter(e.target.value)}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="all">All platforms</option>
                      {platformOptions.map((platform) => (
                        <option key={platform} value={platform}>
                          {platform}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={handleExportUsers}
                disabled={filteredUsers.length === 0}
                className="flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-sm dark:text-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Download className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
                Export
              </button>
            </div>
          </div>
        </div>
        
        <div className="ma-table-scroll scrollbar-light">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Platform
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Last Active
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <Loader2 className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin" />
                      <p className="text-gray-500 dark:text-gray-400 text-sm">Loading users...</p>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <p className="text-red-500 dark:text-red-400 text-sm">{error}</p>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <p className="text-gray-500 dark:text-gray-400 text-sm">
                        {searchText || hasActiveFilters
                          ? 'No users found matching your search or filters.'
                          : 'No users found.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.userId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer" onClick={() => handleViewUser(user)}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                          {user.photo ? (
                            <img
                              src={user.photo}
                              alt={user.name || user.phone_number || 'User'}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-primary-800 dark:text-primary-300 font-semibold text-xs flex items-center justify-center">
                              {user.name && user.name.trim().length > 0
                                ? user.name.charAt(0).toUpperCase()
                                : (user.phone_number ?? 'NA').slice(-4)}
                            </span>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.name && user.name.trim().length > 0
                              ? user.name
                              : user.phone_number ?? 'NA'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {user.phone_number && user.phone_number.trim().length > 0
                              ? user.phone_number
                              : 'NA'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {user.location && user.location.trim().length > 0
                        ? user.location
                        : user.city && user.city.trim().length > 0
                        ? user.city
                        : 'NA'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.status 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                      }`}>
                        {user.status ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {user.platforms && user.platforms.trim().length > 0
                        ? user.platforms
                        : 'NA'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {user.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleViewUser(user)}
                          className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => handleToggleBlock(user, e)}
                          disabled={updatingStatusUserId === user.userId}
                          className={`p-1 transition-colors disabled:opacity-50 ${
                            user.status
                              ? 'text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300'
                              : 'text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300'
                          }`}
                          title={user.status ? 'Block user' : 'Unblock user'}
                        >
                          {updatingStatusUserId === user.userId ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : user.status ? (
                            <Ban className="w-4 h-4" />
                          ) : (
                            <CheckCircle2 className="w-4 h-4" />
                          )}
                        </button>
                        <button 
                          onClick={(e) => handleDeleteClick(user, e)}
                          className="p-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {!loading && !error && (
          <div className="flex flex-col gap-3 border-t px-4 py-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Showing <span className="font-medium">{filteredUsers.length > 0 ? 1 : 0}</span> to <span className="font-medium">{filteredUsers.length}</span> of <span className="font-medium">{users.length}</span> users
            </div>
            <div className="flex flex-wrap gap-2">
              <button className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                Previous
              </button>
              <button className="px-3 py-1 border border-primary-600 rounded-md text-sm text-white bg-primary-600 hover:bg-primary-700">
                1
              </button>
              <button className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && userToDelete && (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-xl sm:rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Confirm Deletion
                </h3>
                <button 
                  onClick={closeDeleteModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-600 dark:text-gray-300">
                  Are you sure you want to delete <span className="font-semibold">{userToDelete.name}</span>? This action cannot be undone.
                </p>
                {deleteError && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {deleteError}
                  </p>
                )}
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={closeDeleteModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash className="w-4 h-4" />
                      <span>Delete</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage; 
